import moment from "moment";
import { Operators } from "@monolith-forensics/monolith-ui";
import type { Query } from "@monolith-forensics/monolith-ui";

import type { LabAsset, LabAssetType } from "./labAssets.types";

// The UI says "Hardware" for the API's "equipment" enum value (same as
// CreateLabAssetModal). Used by the page toggle, the Asset Type column, and
// the flyout chip.
export const LAB_ASSET_TYPE_LABELS: Record<LabAssetType, string> = {
  equipment: "Hardware",
  software: "Software",
};

// Software rows keep their display name in `license_name` (`name` is null);
// equipment rows use `name`. Mirrors COALESCE(license_name, name) in the API.
// Used by the table's Name column and the details page header/breadcrumb.
export const getLabAssetDisplayName = (rowData: LabAsset) =>
  (rowData.asset_type === "software"
    ? rowData.license_name ?? rowData.name
    : rowData.name ?? rowData.license_name) ?? "";

// "Due" window for the validation badge (ENG-1680). Matches the API's first
// reminder email bucket (VALIDATION_DUE_BUCKETS in monolith-api
// validations.due-queries.ts) so the badge appears the day the first email
// goes out.
export const VALIDATION_DUE_SOON_DAYS = 30;

export type ValidationDueStatus = "overdue" | "due" | null;

export const todayIsoDate = () => moment().format("YYYY-MM-DD");

/**
 * Badge state for an asset's `next_validation_due` (`YYYY-MM-DD` or null).
 * Dates are compared as strings: the format is fixed-width and zero-padded,
 * so lexical order is chronological. `today` is injectable for tests.
 */
export const getValidationDueStatus = (
  nextDue: string | null | undefined,
  today: string = todayIsoDate()
): ValidationDueStatus => {
  if (!nextDue) return null;
  if (nextDue < today) return "overdue";
  const windowEnd = moment(today)
    .add(VALIDATION_DUE_SOON_DAYS, "days")
    .format("YYYY-MM-DD");
  return nextDue <= windowEnd ? "due" : null;
};

/**
 * The one-click "Validation overdue" filter (ENG-1680) is a virtual field:
 * the API has no overdue column, only `next_validation_due`. A Yes/No rule on
 * it is rewritten here, before toApiFilter, into the date comparison the API
 * expects (PR #703: overdue is `next_validation_due < today`). "No" becomes
 * `>= today`, which also drops assets with no due date (NULL never compares).
 * mfui has no single-select input, so Yes and No can both be ticked; that
 * means "either", so the rule is dropped rather than resolved by click order.
 */
export const VALIDATION_OVERDUE_FIELD = "validation_overdue";

/**
 * Asset Type rules only belong on the All view (ENG-2295). The API ANDs a rule
 * with the page toggle's top-level `asset_type`, so on Hardware or Software it
 * could only repeat the toggle or empty the table. Returns the same object when
 * there's nothing to drop, so callers can compare by identity.
 */
export const dropAssetTypeRules = (filter: Query): Query =>
  filter.rules.some((rule) => rule.dataField === "asset_type")
    ? {
        ...filter,
        rules: filter.rules.filter((rule) => rule.dataField !== "asset_type"),
      }
    : filter;

export const resolveOverdueFilter = (
  filter: Query | undefined,
  today: string = todayIsoDate()
): Query | undefined => {
  if (!filter) return undefined;
  return {
    ...filter,
    rules: filter.rules.flatMap((rule) => {
      if (rule.dataField !== VALIDATION_OVERDUE_FIELD) return [rule];
      const yes = rule.value?.includes("1");
      const no = rule.value?.includes("0");
      if (yes && no) return [];
      return [
        {
          ...rule,
          dataField: "next_validation_due",
          operator: no ? Operators.GreaterThanOrEqual : Operators.LessThan,
          value: [today],
        },
      ];
    }),
  };
};
